import { useState, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { Download, Plus, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { useAuthStore } from '../stores/auth'
import { AntetPagina } from '../components/ui/AntetPagina'
import { Button } from '../components/ui/Button'
import { CardCifra } from '../components/ui/CardCifra'
import { useConfirmare } from '../components/ui/Confirmare'
import { Explicatie } from '../components/ui/Explicatie'
import { Modal } from '../components/ui/Modal'
import { CapSortabil, useSortare } from '../components/ui/Sortare'
import { StareLista } from '../components/ui/StareLista'
import { TON, type Ton } from '../components/ui/TonuriB4L'

/**
 * Ghidul pieselor comune (etapa A a update-ului de design, 12.10.2026). Arata fiecare piesa,
 * in tema curenta, cu date inventate — ca sa se vada cum arata inainte sa fie folosita pe o
 * pagina reala, si ca regula din `docs/design.md` sa aiba un exemplu viu.
 */
export function GhidUiPage() {
  const rol = useAuthStore((s) => s.user?.role)
  if (rol !== 'admin') return <Navigate to="/dashboard" replace />

  return (
    <div className="grid gap-8">
      <AntetPagina
        titlu="Ghid UI"
        descriere="Piesele comune ale CRM-ului, cu date inventate. Schimbă tema din meniul tău ca să le vezi în ambele."
        explicatie="Regulile scrise stau în docs/design.md. Orice pagină nouă sau mutată pe stilul nou folosește piesele de aici, nu variante proprii."
        actiuni={
          <>
            <Button variant="secondary">
              <Download size={14} /> Exportă
            </Button>
            <Button>
              <Plus size={14} /> Adaugă
            </Button>
          </>
        }
      />
      <SectiunePaleta />
      <SectiuneText />
      <SectiuneCifre />
      <SectiuneTabel />
      <SectiuneStari />
      <SectiuneFerestre />
    </div>
  )
}

function Sectiune({ titlu, cod, children }: { titlu: string; cod: string; children: ReactNode }) {
  return (
    <section className="grid gap-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-sectiune text-b4l-text">{titlu}</h2>
        <code className="text-mic text-b4l-text-dim">{cod}</code>
      </div>
      {children}
    </section>
  )
}

const SENSURI: [Ton, string, string][] = [
  ['verde', 'Verde', 'gata, în regulă, bani încasați'],
  ['albastru', 'Albastru', 'în curs, informație, comenzi'],
  ['galben', 'Galben', 'atenție, așteaptă pe cineva'],
  ['rosu', 'Roșu', 'problemă, întârziere, ștergere'],
  ['mov', 'Mov', 'oameni: clienți, parteneri'],
  ['gri', 'Gri', 'închis, arhivat, fără stare'],
]

function SectiunePaleta() {
  return (
    <Sectiune titlu="Culorile și ce înseamnă" cod="TonuriB4L.ts">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {SENSURI.map(([ton, nume, sens]) => (
          <div
            key={ton}
            className={`grid gap-1 rounded-caseta border border-l-4 border-b4l-border p-3 ${TON[ton].bara} ${TON[ton].fundal}`}
          >
            <span className={`text-corp font-semibold ${TON[ton].text}`}>{nume}</span>
            <span className={`text-mic ${TON[ton].text} opacity-80`}>{sens}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['bg-b4l-app', 'Fundal aplicație'],
          ['bg-b4l-surface', 'Casetă'],
          ['bg-b4l-surface-ridicata', 'Ce plutește'],
          ['bg-b4l-bg', 'Zonă discretă'],
        ].map(([clasa, nume]) => (
          <div key={clasa} className="grid gap-1">
            <div className={`h-12 rounded-caseta border border-b4l-border ${clasa}`} />
            <span className="text-mic text-b4l-text-muted">
              {nume} · <code>{clasa}</code>
            </span>
          </div>
        ))}
      </div>
    </Sectiune>
  )
}

function SectiuneText() {
  return (
    <Sectiune titlu="Text" cod="text-titlu · sectiune · corp · mic · eticheta">
      <div className="grid gap-2 rounded-caseta border border-b4l-border bg-b4l-surface p-4">
        <span className="text-titlu">Titlu de pagină — 20px</span>
        <span className="text-sectiune">Titlu de secțiune — 15px</span>
        <span className="text-corp">Text obișnuit și celule de tabel — 13px</span>
        <span className="text-mic text-b4l-text-muted">Detaliu sub o cifră, notă — 12px</span>
        <span className="text-eticheta uppercase text-b4l-text-dim">Etichetă scurtă — 11px</span>
        <span className="text-corp">
          Explicație la hover:{' '}
          <Explicatie text="Venitul din produse, fără transport. Comenzile anulate și cele eșuate la livrare nu intră.">
            Venit produse
          </Explicatie>
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button>Principal</Button>
        <Button variant="secondary">Secundar</Button>
        <Button variant="ghost">Discret</Button>
        <Button variant="danger">Șterge</Button>
        <Button loading>Se salvează</Button>
      </div>
    </Sectiune>
  )
}

function SectiuneCifre() {
  const [ales, setAles] = useState('tranzit')
  return (
    <Sectiune titlu="Carduri de cifre" cod="CardCifra.tsx">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <CardCifra
          ton="albastru"
          eticheta="Comenzi"
          valoare="1.284"
          detaliu="vândute, în perioadă"
        />
        <CardCifra
          ton="verde"
          eticheta="Venit produse"
          valoare="186.420 lei"
          detaliu="fără transport · cu transport: 198.115"
          explicatie="Comenzile anulate și cele eșuate la livrare nu intră."
        />
        <CardCifra ton="galben" eticheta="Discount acordat" valoare="12.380 lei" />
        <CardCifra ton="rosu" eticheta="Comenzi eșuate" valoare="37" detaliu="2,8% din expediate" />
      </div>
      <p className="text-mic text-b4l-text-muted">
        Ca selector — click deschide lista cardului; cel ales se vede imediat:
      </p>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {(
          [
            ['nepreluat', 'galben', 'Nepreluate', '6'],
            ['tranzit', 'albastru', 'În tranzit', '42'],
            ['intarziat', 'rosu', 'Întârziate', '3'],
            ['livrat', 'verde', 'Livrate', '418'],
          ] as const
        ).map(([cheie, ton, eticheta, valoare]) => (
          <CardCifra
            key={cheie}
            ton={ton}
            eticheta={eticheta}
            valoare={valoare}
            onClick={() => setAles(cheie)}
            selectat={ales === cheie}
          />
        ))}
      </div>
    </Sectiune>
  )
}

interface Rand {
  comanda: string
  client: string
  data: string
  valoare: number
  oras: string | null
}

const RANDURI: Rand[] = [
  {
    comanda: '#10421',
    client: 'Ana Popescu',
    data: '2026-10-11',
    valoare: 214.5,
    oras: 'Cluj-Napoca',
  },
  { comanda: '#10418', client: 'Mihai Ionescu', data: '2026-10-10', valoare: 89.9, oras: 'Iași' },
  { comanda: '#10407', client: 'Elena Dobre', data: '2026-10-09', valoare: 1240, oras: null },
  { comanda: '#10399', client: 'Radu Stan', data: '2026-10-08', valoare: 356.2, oras: 'Brașov' },
  { comanda: '#10388', client: 'Ioana Marin', data: '2026-10-06', valoare: 64, oras: 'Arad' },
]

type Col = 'comanda' | 'client' | 'data' | 'valoare' | 'oras'

function SectiuneTabel() {
  const sortare = useSortare<Col>('data', { comanda: 'desc', client: 'asc', oras: 'asc' })
  const randuri = sortare.ordoneaza(RANDURI, (r, k) => r[k])
  const lei = new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2 })
  return (
    <Sectiune titlu="Tabel cu sortare" cod="Sortare.tsx — useSortare + CapSortabil">
      <div className="overflow-x-auto rounded-caseta border border-b4l-border bg-b4l-surface">
        <table className="w-full text-corp">
          <thead className="border-b border-b4l-border bg-b4l-bg-soft text-mic text-b4l-text-muted">
            <tr>
              <CapSortabil sortare={sortare} coloana="comanda">
                Comandă
              </CapSortabil>
              <CapSortabil sortare={sortare} coloana="client">
                Client
              </CapSortabil>
              <CapSortabil sortare={sortare} coloana="data">
                Data
              </CapSortabil>
              <CapSortabil
                sortare={sortare}
                coloana="valoare"
                dreapta
                explicatie="Totalul plătit de client, cu TVA, fără transport."
              >
                Valoare
              </CapSortabil>
              <CapSortabil sortare={sortare} coloana="oras">
                Oraș
              </CapSortabil>
            </tr>
          </thead>
          <tbody>
            {randuri.map((r) => (
              <tr
                key={r.comanda}
                className="border-b border-b4l-border last:border-0 hover:bg-b4l-bg-soft"
              >
                <td className="px-3 py-2 font-medium">{r.comanda}</td>
                <td className="px-3 py-2">{r.client}</td>
                <td className="px-3 py-2 tabular-nums text-b4l-text-muted">
                  {new Date(r.data).toLocaleDateString('ro-RO')}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{lei.format(r.valoare)}</td>
                <td className="px-3 py-2">
                  {r.oras ?? <span className="text-b4l-text-dim">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-mic text-b4l-text-muted">
        Primul clic sortează în direcția firească (cifrele și datele de la cea mai mare, textul
        alfabetic), al doilea inversează. Celulele goale stau mereu la final.
      </p>
    </Sectiune>
  )
}

function SectiuneStari() {
  const [stare, setStare] = useState<'incarca' | 'eroare' | 'gol' | 'date'>('eroare')
  const interogare = {
    isLoading: stare === 'incarca',
    isError: stare === 'eroare',
    error: {
      response: { data: { error: { message: 'ExpertAccounts nu răspunde (timeout 30s).' } } },
    },
    refetch: () => toast.info('Aici s-ar reîncerca interogarea.'),
  }
  return (
    <Sectiune titlu="Stările unei liste" cod="StareLista.tsx">
      <div className="flex flex-wrap gap-1.5">
        {(
          [
            ['incarca', 'Se încarcă'],
            ['eroare', 'Eroare'],
            ['gol', 'Goală'],
            ['date', 'Cu date'],
          ] as const
        ).map(([k, e]) => (
          <button
            key={k}
            type="button"
            aria-pressed={stare === k}
            onClick={() => setStare(k)}
            className={`rounded-full border px-3 py-1 text-mic ${
              stare === k
                ? 'border-b4l-blue bg-b4l-blue-light font-semibold text-b4l-blue-dark'
                : 'border-b4l-border bg-b4l-surface hover:border-b4l-blue'
            }`}
          >
            {e}
          </button>
        ))}
      </div>
      <div className="rounded-caseta border border-b4l-border bg-b4l-surface p-2">
        <StareLista
          interogare={interogare}
          gol={stare === 'gol'}
          mesajGol="Niciun decont în luna aceasta. Deconturile apar după ce un coleg încarcă un bon."
        >
          <p className="p-4 text-corp">Aici ar fi tabelul cu rânduri.</p>
        </StareLista>
      </div>
    </Sectiune>
  )
}

function SectiuneFerestre() {
  const { confirma, cere } = useConfirmare()
  const [modal, setModal] = useState(false)
  return (
    <Sectiune titlu="Ferestre" cod="Confirmare.tsx · Modal.tsx">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="danger"
          onClick={async () => {
            const da = await confirma({
              titlu: 'Ștergi decontul #214?',
              mesaj: 'Bonurile atașate se șterg odată cu el. Acțiunea nu se poate anula.',
              buton: 'Șterge decontul',
              pericol: true,
            })
            toast(da ? 'Confirmat — aici s-ar șterge.' : 'Renunțat.')
          }}
        >
          Confirmare cu pericol
        </Button>
        <Button
          variant="secondary"
          onClick={async () => {
            const da = await confirma({
              titlu: 'Trimiți 12 încasări în ExpertAccounts?',
              mesaj: 'Se creează câte o notă contabilă pentru fiecare.',
              buton: 'Trimite',
            })
            toast(da ? 'Confirmat.' : 'Renunțat.')
          }}
        >
          Confirmare simplă
        </Button>
        <Button
          variant="secondary"
          onClick={async () => {
            const motiv = await cere({
              titlu: 'Respingi cererea de marfă?',
              eticheta: 'Motivul — îl vede cel care a făcut cererea',
              buton: 'Respinge',
              obligatoriu: true,
              lung: true,
            })
            toast(motiv === null ? 'Renunțat.' : `Motiv: ${motiv}`)
          }}
        >
          Cere un text
        </Button>
        <Button variant="secondary" onClick={() => setModal(true)}>
          <RefreshCw size={14} /> Fereastră obișnuită
        </Button>
      </div>
      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title="Fereastră obișnuită"
        footer={<Button onClick={() => setModal(false)}>Închide</Button>}
      >
        <p className="text-corp text-b4l-text-muted">
          Pentru formulare și detalii scurte. Detaliile unei entități (o comandă, un colet) se
          deschid în panoul din dreapta, nu aici.
        </p>
      </Modal>
    </Sectiune>
  )
}
